import React, {useState} from 'react';

import get from 'lodash/get';

import {FormErrorsContext, FormErrorsContextType} from './form-errors-context';

import type {SerializedError} from '@reduxjs/toolkit';
import type {FetchBaseQueryError} from '@reduxjs/toolkit/dist/query';

export type FormErrorsProps = FormErrorsContextType;

export const withFormErrors = <P extends FormErrorsProps>(
  FormsErrorProvidedWrappedComponent: React.ComponentType<P>
): React.ComponentType<Omit<P, keyof FormErrorsProps>> => {
  const FormErrorsConsumer = (props: P) => (
    <FormErrorsContext.Consumer>
      {(formErrors) => (
        <FormsErrorProvidedWrappedComponent {...formErrors} {...props} />
      )}
    </FormErrorsContext.Consumer>
  );

  return FormErrorsConsumer;
};

export const createFormErrorsContext = <P extends FormErrorsProps>(
  FormErrorsContextUserComponent: React.ComponentType<P>
): React.ComponentType<Omit<P, keyof FormErrorsProps>> => {
  const ChildrenWithFormErrors = withFormErrors<P>(
    FormErrorsContextUserComponent
  );

  return function FormErrorsProvider(props: P) {
    const [formErrors, setFormErrors] = useState({});

    const captureFormErrorsFromRejection = (
      err?: SerializedError | FetchBaseQueryError | Error
    ) => {
      if (get(err, 'response.status', get(err, 'status')) === 400) {
        setFormErrors(get(err, 'data', {}));
      }
    };

    const errorsContext: FormErrorsContextType = {
      setFormErrors,
      captureFormErrorsFromRejection,
      formErrors,
    };

    return (
      <FormErrorsContext.Provider value={errorsContext}>
        <ChildrenWithFormErrors {...props} />
      </FormErrorsContext.Provider>
    );
  };
};
